// Phase E observability — Last-sync status card for the Broker Activity
// landing page. Reads the company's active `crm_connections` (via the
// existing `getCrmConnectionStatus` callable) and surfaces:
//   - colored status pill (green ok / amber partial / red failed)
//   - dealsSynced, advisersSynced, lastSyncAt quick stats
//   - collapsible "last 3 errors" list
//
// Design notes:
//   - Pure read-only: no mutation, no triggerSync button (that lives on
//     /leads/integrations where it belongs).
//   - Reuses styling primitives from PipelinePage.tsx (rounded-lg border
//     gray-700, bg-gray-800/40) for visual continuity with the rest of
//     the broker-activity subapp.
//   - Falls back gracefully when no connections exist (Damien pre-connect
//     state) — explains how to connect via /leads/integrations.

import React, { useMemo, useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Plug,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  useCrmConnection,
  type CrmConnectionStatus,
} from '../../hooks/useCrmConnection';

interface Props {
  companyId: string | undefined;
  // Current caller's role. Marketing can't operate the MyCRM credential
  // surface, so the pre-connect empty state points them at an admin and
  // drops the link to the connection screen (marketing RBAC section 6).
  role?: string;
}

type Severity = 'ok' | 'partial' | 'failed' | 'unknown';

function severityOf(c: CrmConnectionStatus): Severity {
  const r = (c.lastSyncResult || '').toLowerCase();
  if (r === 'success') return 'ok';
  if (r === 'partial') return 'partial';
  if (r === 'failed') return 'failed';
  return 'unknown';
}

function severityCopy(s: Severity): { label: string; pillClass: string; Icon: React.FC<{ className?: string }> } {
  switch (s) {
    case 'ok':
      return {
        label: 'Healthy',
        pillClass: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40',
        Icon: CheckCircle2,
      };
    case 'partial':
      return {
        label: 'Partial — investigate',
        pillClass: 'bg-amber-500/10 text-amber-300 border-amber-500/40',
        Icon: AlertTriangle,
      };
    case 'failed':
      return {
        label: 'Failed',
        pillClass: 'bg-red-500/10 text-red-300 border-red-500/40',
        Icon: XCircle,
      };
    default:
      return {
        label: 'No sync yet',
        pillClass: 'bg-gray-700/40 text-gray-400 border-gray-600',
        Icon: Plug,
      };
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return 'just now';
    const mins = Math.floor(ms / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(iso).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return iso;
  }
}

export const CrmSyncStatusCard: React.FC<Props> = ({ companyId, role }) => {
  const { connections, loading, error } = useCrmConnection(companyId);
  const [errorsOpen, setErrorsOpen] = useState<boolean>(false);
  const isMarketing = role === 'marketing';

  // Surface the most recent active connection (Entourage will have 1 — MyCRM).
  // If multiple exist (multi-CRM in future), prefer the one with the most
  // recent lastSyncAt to keep the card deterministic.
  const primary = useMemo<CrmConnectionStatus | null>(() => {
    const active = connections.filter((c) => c.status === 'active');
    if (active.length === 0) return null;
    return active.reduce((best, cur) => {
      const bt = best.lastSyncAt ? new Date(best.lastSyncAt).getTime() : 0;
      const ct = cur.lastSyncAt ? new Date(cur.lastSyncAt).getTime() : 0;
      return ct > bt ? cur : best;
    });
  }, [connections]);

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
        <div className="text-[11px] uppercase tracking-wider text-gray-400">
          CRM sync status
        </div>
        <div className="mt-2 text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
        Failed to load CRM connection status: {error}
      </div>
    );
  }

  if (!primary) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
        <div className="flex items-center gap-2">
          <Plug className="w-4 h-4 text-gray-400" />
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            CRM sync status
          </div>
        </div>
        {isMarketing ? (
          <div className="mt-2 text-sm text-gray-300">
            No active CRM connection. Ask an admin to connect your CRM to
            populate pipeline data.
          </div>
        ) : (
          <div className="mt-2 text-sm text-gray-300">
            No active CRM connection. Connect MyCRM (or Monday.com) on{' '}
            <a
              href="/leads/integrations"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              /leads/integrations
            </a>{' '}
            to populate trail-leakage detection and pipeline data.
          </div>
        )}
      </div>
    );
  }

  const severity = severityOf(primary);
  const { label, pillClass, Icon } = severityCopy(severity);
  const errors = primary.lastSyncErrors ?? [];
  const lastThreeErrors = errors.slice(-3).reverse(); // newest first

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            CRM sync status
          </div>
          <div className="mt-1 text-sm text-gray-200">
            {primary.provider.toUpperCase()} · last sync{' '}
            <span className="text-white font-semibold">
              {formatRelative(primary.lastSyncAt)}
            </span>
          </div>
        </div>
        <div
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold ${pillClass}`}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </div>
      </div>

      {/* Quick stats — three column on lg+, stacked on small */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded border border-gray-700 bg-gray-900/30 p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">
            Deals
          </div>
          <div className="text-2xl font-bold text-white">
            {primary.dealsSynced.toLocaleString()}
          </div>
        </div>
        <div className="rounded border border-gray-700 bg-gray-900/30 p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">
            Advisers
          </div>
          <div className="text-2xl font-bold text-white">
            {primary.advisersSynced.toLocaleString()}
          </div>
        </div>
        <div className="rounded border border-gray-700 bg-gray-900/30 p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">
            Result
          </div>
          <div className="text-2xl font-bold text-white capitalize">
            {primary.lastSyncResult || '—'}
          </div>
        </div>
      </div>

      {/* Collapsible error list */}
      {errors.length > 0 && (
        <div className="mt-4 rounded border border-gray-700 bg-gray-900/30">
          <button
            type="button"
            onClick={() => setErrorsOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-left text-xs text-gray-300 hover:bg-gray-800/40"
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              {errors.length} error{errors.length === 1 ? '' : 's'} from last sync
            </span>
            {errorsOpen ? (
              <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
            )}
          </button>
          {errorsOpen && (
            <ul className="px-3 pb-3 space-y-1.5 text-xs text-red-300 font-mono">
              {lastThreeErrors.map((e, i) => (
                <li
                  key={`${i}-${e.slice(0, 32)}`}
                  className="border-l-2 border-red-500/40 pl-2 break-all"
                >
                  {e}
                </li>
              ))}
              {errors.length > 3 && (
                <li className="text-gray-500 italic">
                  …{errors.length - 3} earlier error(s) suppressed for brevity.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CrmSyncStatusCard;
